import testProject from '../testProject.js'

import {renewalOptionData} from "./renewalOptionData";
import { pointMaps, distressInfo, severityCodes } from './../../js/distress/distressTypeData'
import {
  chipCapeTrafficLevels,
  chipCapeTurningMovements,
  chipCapeAlligator,
  chipCapeTransverseLongitudinal,
  chipCapeBleedFlush,
  chipCapeAC1,
  chipCapeAC2,
  chipCapeAC3,
  // ChipCapeAC4 is deprecated as redundant and is not used
  chipCapeAC5
} from './../../js/validation/chipCapeWarnings'
import {
  thinHmaTurningMovements,
  thinHmaGeosynthetics,
  thinHmaCurb,
  thinHmaAC1,
  // thinHmaAC2, Deprecated as redundant
  thinHmaAC3
} from './../../js/validation/thinHMAWarnings'
import {
  fullDepthLimitations
} from './../../js/validation/fullDepthWarnings'
import {
  fullReconCbrMinimum,
} from './../../js/validation/fullReconWarnings'
/*

This file defines the point maps and evaluation function that generate the final RxPave
recommendations.

Source of scores is TIB UW Research Part 4 Project Scoping.pdf file

The point map structure is:

    renewalOptionPointMap = {
      distressType: {
        0: notPresentPoints,
        1: lowSeverityPoints, 
        2: medSeverityPoints,
        3: highSeverityPoints
      }
    }

The intent is to use the distressTypeSeverity score from a project Object to index into the
distressType entry in the pointMap object to get the number of points.

The number of points are mapped as follows:
  Recommended: 2 
  Conditional: 1
  Not recommended: -1
 */

// Map between numerical severity value and display code

export function createWarnings(renewalOption, project, allRenewalOptionScores){
  const warnings = [];

  const currentOptionScores = allRenewalOptionScores.filter(e => e.renewalOption === renewalOption)[0];

  if (renewalOption === 'chipCape' && currentOptionScores['isViable']){
    warnings.push(chipCapeTrafficLevels(project));
    warnings.push(chipCapeTurningMovements(project));
    warnings.push(chipCapeAlligator(project));
    warnings.push(chipCapeTransverseLongitudinal(project));
    warnings.push(chipCapeBleedFlush(project));
    warnings.push(chipCapeAC1(project));
    warnings.push(chipCapeAC2(project));
    warnings.push(chipCapeAC3(project));
    warnings.push(chipCapeAC5(project));
  }

  if (renewalOption === 'thinHMA' && currentOptionScores['isViable']){
    const chipCapeScore = allRenewalOptionScores.filter(e => e.renewalOption === 'chipCape')[0]['score'];
    const thinHmaScore = allRenewalOptionScores.filter(e => e.renewalOption === 'thinHMA')[0]['score'];
    warnings.push(thinHmaTurningMovements(project));
    warnings.push(thinHmaGeosynthetics(project));
    warnings.push(thinHmaCurb(project));
    warnings.push(thinHmaAC1(project, chipCapeScore, thinHmaScore));
    warnings.push(thinHmaAC3(project, chipCapeScore, thinHmaScore));
  }

  if (renewalOption === 'fullDepth'){
    warnings.push(fullDepthLimitations(project));
  }

  if (renewalOption === 'fullRecon' ){
    warnings.push(fullReconCbrMinimum(project));
  }

  return warnings.filter((e) => e !== null && e !== undefined)
}



export function renewalOptionScore(project, renewalOption){
  let score = 0;
  let renewalIsViable = true;
  let distressScores = {};
  let nWarnings = 0;
  const renewalOptionPointMap = pointMaps[renewalOption];
  const data = renewalOptionData[renewalOption];

  // Tally points and ID disqualifying factors
  Object.entries(renewalOptionPointMap).forEach(distress => {
    const distressType = distress[0]; // distressType
    const pointMap = distress[1]; // pointMap

    let distressIsDisqualifying = false;

    // Get the severity of the distress type
    let distressSeverityKey = Object.keys(project).filter(k => {
      return k.startsWith(distressType) && k.endsWith('Severity')
    })[0];

    let distressKey = distressSeverityKey.substring(0, distressSeverityKey.indexOf("Severity"));

    const distressSeverity = project[distressSeverityKey];
    let points = pointMap[distressSeverity];

    // Disqualify the renewal option if any distress type
    if (points === -1) {
      renewalIsViable = false;
      distressIsDisqualifying = true;
      nWarnings += 1;
      // score += points;
      // points = -1;
    } else {
      score += points
    }
    // console.log(renewalOption, distressKey, points, !renewalIsAppropriate);

    // const dInfo = distressInfo[distressType];

    distressScores[distressKey] = {
      // name: dInfo['name'],
      key: distressKey,
      // description: dInfo['descriptionBasic'],
      points: points,
      severity: distressSeverity,
      severityCode: severityCodes[distressSeverity],
      isDisqualifying: distressIsDisqualifying,
      nWarnings: nWarnings
    };
  });

  // Do PCR check
  const pcrIsOk = project.lastPCRReading <= data.maxPCR && project.lastPCRReading >= data.minPCR;


  return {
    renewalOption: renewalOption,
    pcrScore: project.lastPCRReading,
    score: score,
    distressScores: distressScores,
    optionData: data,
    recommendations: [],
    warnings: null,

    // Viability settings. Null values require comparison with other renewal options.
    isViable: null,                   // Global viability
    scoreAllowsRecommendation: null,  // Top score or within 1 point of top
    distressViable: renewalIsViable,  // No disqualifying distresses
    pcrIsOk: pcrIsOk,                 // Pcr within range for this renewal option
  }
}

/*
Calculate scores for all renewal options and return them in a structure.
 */
export function getAllRenewalOptionScores(project){

  const results = [];

  // For each renewal option type generate its score
  Object.keys(pointMaps).forEach(k => {
    results.push(renewalOptionScore(project, k))
  });

  // Set validity elements that rely on global knowledge of all options and their relative scores
  const scoreList = results.map((r) => r.score);
  const topScore = Math.max(...scoreList);

  // Set final viability flags
  results.forEach((renewalOptionResult) => {
    renewalOptionResult.scoreAllowsRecommendation = renewalOptionResult.score >= (topScore - 1);
    renewalOptionResult.isViable = renewalOptionResult.pcrIsOk
      && renewalOptionResult.scoreAllowsRecommendation
      && renewalOptionResult.distressViable;
  });


  // Generate warnings after all other flags have been set
  results.forEach(renewalOptionResult => {
    renewalOptionResult.warnings = createWarnings(
      renewalOptionResult.renewalOption,
      project,
      results
    );
  });

  // Disqualify based on warning results if any are disqualifying
  results.forEach(renewalOptionResult => {
    renewalOptionResult.warnings.forEach(w => {
      if (w.isDisqualifying !== undefined && w.isDisqualifying !== null && w.isDisqualifying){
        renewalOptionResult.isViable = false;
      }
    })
  });


  // Sort by validity and points:
  // - Viable first, sorted by score
  // - Nonviable second, sorted by score
  const compFcn = (a, b) => b.score - a.score;
  const viableOptions = results.filter((r) => r.isViable).sort(compFcn);
  const notViableOptions = results.filter((r) => !r.isViable).sort(compFcn);

  return viableOptions.concat(notViableOptions);
}
